import { ProjectDocumentationForm, FileItem, FileTag } from '../types/projectDocumentation.ts';
import { VALIDATION_RULES } from '../constants/projectDocumentation.ts';

const REQUIRED_DOCUMENTS_ANTEPROYECTO = [
  { tagName: 'Carátula', label: 'Carátula', note: 'Imagen o video (se mostrará siempre primero)' },
  { tagName: 'Plano', label: 'Plano', note: null },
  { tagName: 'Vista', label: 'Vista', note: null },
  { tagName: 'Render exterior', label: 'Render exterior', note: null },
  { tagName: 'Memoria descriptiva', label: 'Memoria descriptiva', note: null }
];

const REQUIRED_DOCUMENTS_PROYECTO = [
  { tagName: 'Carátula', label: 'Carátula', note: 'Imagen o video (se mostrará siempre primero)' },
  { tagName: 'Plano', label: 'Plano', note: null },
  { tagName: 'Vista', label: 'Vista', note: null },
  { tagName: 'Render exterior', label: 'Render exterior', note: null },
  { tagName: 'Memoria descriptiva', label: 'Memoria descriptiva', note: null },
  { tagName: 'Cómputo métrico', label: 'Cómputo métrico', note: null },
  { tagName: 'Especificaciones técnicas', label: 'Especificaciones técnicas', note: null }
];

export const validateProjectDocumentationForm = (
  formData: ProjectDocumentationForm,
  fileItems: FileItem[],
  fileTags: FileTag[],
  projectType: 'proyecto' | 'anteproyecto'
): { isValid: boolean; errors: Record<string, string>; firstErrorField?: string } => {
  const errors: Record<string, string> = {};
  let firstErrorField: string | undefined;

  const REQUIRED_DOCUMENTS = projectType === 'proyecto' 
    ? REQUIRED_DOCUMENTS_PROYECTO 
    : REQUIRED_DOCUMENTS_ANTEPROYECTO;

  if (!formData.name.trim()) {
    errors.name = 'El nombre es requerido';
    if (!firstErrorField) firstErrorField = 'name';
  } else if (formData.name.length < VALIDATION_RULES.name.minLength) {
    errors.name = `El nombre debe tener al menos ${VALIDATION_RULES.name.minLength} caracteres`;
    if (!firstErrorField) firstErrorField = 'name';
  }

  if (!formData.description.trim()) {
    errors.description = 'La descripción es requerida';
    if (!firstErrorField) firstErrorField = 'description';
  } else if (formData.description.length < VALIDATION_RULES.description.minLength) {
    errors.description = `La descripción debe tener al menos ${VALIDATION_RULES.description.minLength} caracteres`;
    if (!firstErrorField) firstErrorField = 'description';
  }

  const totalSurfaces = 
    formData.total_surface + 
    formData.covered_surface + 
    formData.semicovered_surface + 
    formData.uncovered_surface;
  
  if (totalSurfaces === 0) {
    errors.total_surface = 'Debe ingresar al menos una superficie mayor a 0';
    errors.covered_surface = 'Debe ingresar al menos una superficie mayor a 0';
    errors.semicovered_surface = 'Debe ingresar al menos una superficie mayor a 0';
    errors.uncovered_surface = 'Debe ingresar al menos una superficie mayor a 0';
    if (!firstErrorField) firstErrorField = 'total_surface';
  }

  if (!formData.environments || formData.environments <= 0) {
    errors.environments = 'Los ambientes son requeridos';
    if (!firstErrorField) firstErrorField = 'environments';
  }

  if (!formData.bedrooms || formData.bedrooms <= 0) {
    errors.bedrooms = 'Los dormitorios son requeridos';
    if (!firstErrorField) firstErrorField = 'bedrooms';
  }

  if (!formData.bathrooms || formData.bathrooms <= 0) {
    errors.bathrooms = 'Los baños son requeridos';
    if (!firstErrorField) firstErrorField = 'bathrooms';
  }

  if (!formData.floors || formData.floors <= 0) {
    errors.floors = 'Los pisos son requeridos';
    if (!firstErrorField) firstErrorField = 'floors';
  }

  if (!formData.implantation || formData.implantation <= 0) {
    errors.implantation = 'La implantación es requerida';
    if (!firstErrorField) firstErrorField = 'implantation';
  } else if (formData.implantation > VALIDATION_RULES.implantation.max) {
    errors.implantation = `La implantación no puede superar ${VALIDATION_RULES.implantation.max} m²`;
    if (!firstErrorField) firstErrorField = 'implantation';
  }

  if (!formData.construction_type) {
    errors.construction_type = 'El tipo de construcción es requerido';
    if (!firstErrorField) firstErrorField = 'construction_type';
  }

  if (!formData.consultation_price || formData.consultation_price <= 0) {
    errors.consultation_price = 'El valor hora de consulta es requerido';
    if (!firstErrorField) firstErrorField = 'consultation_price';
  }

  if (!(formData as any).consultation_price_currency) {
    errors.consultation_price_currency = 'La moneda de consulta es requerida';
    if (!firstErrorField) firstErrorField = 'consultation_price_currency';
  }

  if (!(formData as any).price || (formData as any).price <= 0) {
    errors.price = 'El precio del paquete es requerido';
    if (!firstErrorField) firstErrorField = 'price';
  }

  if (!(formData as any).price_currency) {
    errors.price_currency = 'La moneda del paquete es requerida';
    if (!firstErrorField) firstErrorField = 'price_currency';
  }

  const itemsWithoutTag = fileItems.filter(item => !item.tagId);
  if (itemsWithoutTag.length > 0) {
    errors.files = `Debes asignar una etiqueta a ${itemsWithoutTag.length} archivo(s)`;
    if (!firstErrorField) firstErrorField = 'files';
  }

  const missingRequiredDocs: string[] = [];
  REQUIRED_DOCUMENTS.forEach(doc => {
    const docTag = fileTags.find(tag => {
      const tagNameLower = tag.name.toLowerCase().trim();
      const searchNameLower = doc.tagName.toLowerCase().trim();
      return tagNameLower === searchNameLower || 
             tagNameLower.includes(searchNameLower) || 
             searchNameLower.includes(tagNameLower);
    });
    
    if (docTag) {
      const hasFile = fileItems.some(item => String(item.tagId) === String(docTag.id));
      if (!hasFile) {
        missingRequiredDocs.push(doc.label);
      }
    }
  });

  if (missingRequiredDocs.length > 0) {
    errors.requiredDocs = `Faltan documentos requeridos: ${missingRequiredDocs.join(', ')}`;
    if (!firstErrorField) firstErrorField = 'requiredDocs';
  }

  return { 
    isValid: Object.keys(errors).length === 0, 
    errors,
    firstErrorField 
  };
};

